import { Close } from "@mui/icons-material";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents";
import { getSelectedOrganization } from "../../../helpers/localStorage.ts";
import usePostHog from "../../../hooks/usePostHog.tsx";
import { useAppStore } from "../../../hooks/useStores.tsx";
import { UserOrganization } from "../../../models/organization.ts";
import BriaButton from "../BriaButton/BriaButton.tsx";
import BriaLogo from "../BriaLogo/BriaLogo.tsx";
import LoadingPlaceholder from "../LoadingPlaceholder/LoadingPlaceholder.tsx";
import OrganizationsDropdown from "../OrganizationsDropdown/OrganizationsDropdown.tsx";
import styles from "./SelectOrganizationPopup.module.scss";

interface IProps {
	open: boolean;
	onClose: (selectedOrg: UserOrganization | null | undefined) => Promise<void>;
	onSelect?: (org: UserOrganization | null) => void;
	onSubmit?: () => void;
	description?: string;
	showLogo?: boolean;
	buttonLoading?: boolean;
}

const SelectOrganizationPopup = ({
	buttonLoading = false,
	open,
	onClose,
	onSelect,
	onSubmit,
	description,
	showLogo = false,
}: IProps) => {
	const { authStore } = useAppStore();
	const { t } = useTranslation("translation", { keyPrefix: "selectOrganization" });
	const { trackEvent } = usePostHog();
	const [selectedOrg, setSelectedOrg] = useState<UserOrganization | null>(getSelectedOrganization());

	const handleContinue = () => {
		// Track organization selection confirmation
		if (selectedOrg) {
			trackEvent(POSTHOG_EVENTS.ORGANIZATION_SELECTED, {
				org_id: selectedOrg.organization.uid,
				org_name: selectedOrg.organization.name,
				org_role: selectedOrg.role,
			});
		}

		if (onSubmit) {
			onSubmit();
		} else {
			onClose?.(selectedOrg);
		}
	};

	const handleChangeOrg = (org: UserOrganization | null) => {
		if (onSelect) {
			onSelect(org);
		} else {
			setSelectedOrg(org);
		}
	};

	return (
		<Dialog
			classes={{
				paper: styles.paper,
			}}
			onClose={() => onClose(null)}
			open={open}
			maxWidth={false}
		>
			<Box className={styles.container}>
				<IconButton onClick={() => onClose(null)} className={styles.closeButton}>
					<Close />
				</IconButton>
				<Box className={styles.content}>
					{showLogo && <BriaLogo />}
					<Typography className={styles.title}>{t("header")}</Typography>
					<Typography className={styles.description}>{description ?? t("descriptionCheckout")}</Typography>
					<LoadingPlaceholder className={styles.loaderStyle} isLoading={authStore.isLoadingUserOrganizations}>
						<Box className={styles.form}>
							<OrganizationsDropdown
								reload={false}
								placeholder={t("dropdownPlaceholder")}
								onOrgChange={handleChangeOrg}
								className={styles.orgsDropdown}
							/>
							<BriaButton
								buttonType="primary"
								onClick={handleContinue}
								className={styles.primaryBtn}
								fullWidth={true}
								disabled={!selectedOrg || buttonLoading}
								loading={buttonLoading}
							>
								{t("primaryBtn")}
							</BriaButton>
						</Box>
					</LoadingPlaceholder>
				</Box>
			</Box>
		</Dialog>
	);
};

export default observer(SelectOrganizationPopup);
